import React from "react";

export default function CamperSolarBlogEn() {
  return (
    <>
      <p>
        During the winter break I looked into how I could add a solar panel to my camper van. I thought it through
        in several steps and actually went ahead and installed it. The whole process is also documented in four
        YouTube videos linked above. But here are the steps — the details are in the videos.
      </p>


      <div className="blog-note">
        <em>
          <strong>Important!</strong> This blog shows electrical circuits. The principles and schematics have been
          carefully reviewed, but I cannot guarantee their correctness — in particular with regard to compliance
          with all applicable regulations. When working with electrical systems, a qualified professional should
          always be consulted due to the risk to health and life.
        </em>
      </div>

      <p><strong>The panel</strong></p>
      <p>
        The first question I asked myself was: how much power do I actually need, and will it fit on the roof?
        After studying numerous videos and blog posts I worked out how much capacity I needed — for lighting,
        charging a phone, occasionally running a laptop, the water pump, the heating and fridge controls … all
        fairly modest — oh, the hair dryer — yes, that needs a lot … and 230 V. All in all I reckoned I could get
        away with 120 Wp to 180 Wp for the panel. Depending on how much you want to spend, that also dictates what
        fits on the roof — the more compact the panel, the more expensive it gets. While a 400 Wp panel for a house
        roof can be had for around €50, I paid €350 for a 175 Wp camper panel.
      </p>
      <p>
        So what is the difference? Partly size — a standard house-roof module simply would not fit on my van. You
        also have to consider that a camper experiences far more vibration than a house roof, and a hairline crack
        can destroy the panel — the larger it is, the more freely it flexes. On top of that, camper panels benefit
        from a wider voltage range, which helps squeeze out a few more watts at dusk. I went with a specialist
        high-voltage panel. Then there are the bypass diodes: a typical house-roof module has one; mine has three,
        meaning it can still generate power under partial shading. Finally, house-roof panels are produced in
        massive volumes — that keeps the price down.
      </p>

      <p><strong>The solar charge controller</strong></p>
      <p>
        You also need a solar charge controller, which charges the leisure battery when the sun is shining. It
        converts the current from the roof into charging current and detects when the battery is full. It needs to
        match the panel in terms of voltage and current range. For me, Bluetooth was also a must so I could monitor
        the values remotely.
      </p>

      <p><strong>The battery</strong></p>
      <p>
        For this setup a 150 Ah battery — 1,800 Wh — made sense, and it should be a lithium iron phosphate
        (LiFePO4) battery. The advantages: it is lighter than a lead-acid battery, has a higher usable capacity,
        and uses that capacity more efficiently. And to make the state of charge easy to read, Bluetooth support
        was a requirement here too.
      </p>

      <p><strong>The inverter</strong></p>
      <p>
        For the hair dryer I also added a 1,500 W inverter — that is the minimum the dryer needs. This
        significantly increased the complexity because — if you want to power the standard on-board sockets from
        it — a mains-priority switch becomes mandatory. Without it, the solar current and the shore power will
        collide.
      </p>

      <img
        src="/images/Camper-Solar-Schaltung.png"
        alt="Wiring diagram of the solar installation in the camper van"
        className="blog-img"
      />

      <p>
        I routed the cable from the panel through the roof vent (Midi-Heki) — I did not want to drill through the
        roof itself. From there it runs hidden behind the trim panels to the fuse box. Depending on where that is
        in your camper, this can be more or less work. I needed about 5 m of cable.
      </p>

      <p><strong>The cables</strong></p>
      <p>
        Each type of current requires its own cable type. Solar cable for the connection from the panel on the roof —
        UV-resistant and thickly insulated (house-roof systems often run at voltages &gt; 800 V; here in the camper
        it is only 60 V). For the connection from the solar charge controller to the battery: standard FLRY-B
        automotive cable — in my case 4 mm². To the inverter: heavy FLY 25 mm² battery cable, kept as short as
        possible. And behind the inverter: 1.5 mm² stranded mains cable.
      </p>

      <p><strong>The monitor display</strong></p>
      <p>
        Once everything was installed I also built a display to monitor the batteries and the solar charge
        controller. You can find it — including a live demo — elsewhere on this site.
      </p>
    </>
  );
}
